import { memo, useCallback } from 'react';
import {
  Grid,
  S6,
  TypographyWithHelp,
  useTranslations,
  WidgetContainer,
} from '@uniqkey-frontend/shared-app';
import {
  GetOrganizationByIdResponse,
  EmployeeAccountStatus,
} from '@uniqkey-backend-partner/api-client';
import { useNavigate, createSearchParams } from 'react-router-dom';
import OrganizationUserDetailsWidgetItem from './components/OrganizationUserDetailsWidgetItem';
import PageRouteEnum from '../../../../../../enums/PageRouteEnum';
import { OrganizationModuleTabEnum } from '../../../OrganizationModule';

interface IOrganizationUserDetailsWidgetProps {
  organization: GetOrganizationByIdResponse;
  isLoading: boolean;
  isError: boolean;
}

const TITLE_TYPOGRAPHY_PROPS = { variant: 'subtitle1' };

const OrganizationUserDetailsWidget = (props: IOrganizationUserDetailsWidgetProps) => {
  const {
    organization, isLoading, isError,
  } = props;
  const { t } = useTranslations();
  const navigate = useNavigate();

  const {
    organizationId,
    activatedEmployeesCount,
    provisionedEmployeesCount,
  } = organization;

  const handleNavigation = useCallback((statusFilter: EmployeeAccountStatus) => {
    const search = createSearchParams({ statusFilter }).toString();
    navigate({
      pathname: `${
        PageRouteEnum.Organizations
      }/${organizationId}/${OrganizationModuleTabEnum.Employees}`,
      search,
    });
  }, [organizationId, navigate]);

  const handleActiveUsersLinkClick = useCallback(() => {
    handleNavigation(EmployeeAccountStatus.Active);
  }, [handleNavigation]);

  const handleInvitedUsersLinkClick = useCallback(() => {
    handleNavigation(EmployeeAccountStatus.Invited);
  }, [handleNavigation]);

  if (isError || !organization) {
    return null;
  }

  return (
    <WidgetContainer container withShadow isLoading={isLoading} rowGap={2}>
      <Grid item>
        <TypographyWithHelp
          helperText={t('organizationUserDetailsWidget.tooltip')}
          color={S6}
          TypographyProps={TITLE_TYPOGRAPHY_PROPS}
        >
          {t('organizationUserDetailsWidget.title')}
        </TypographyWithHelp>
      </Grid>
      <Grid item container>
        <OrganizationUserDetailsWidgetItem
          onClick={handleActiveUsersLinkClick}
          title={t('organizationUserDetailsWidget.activeUsers.title')}
          titleHelperText={t('organizationUserDetailsWidget.activeUsers.tooltip')}
          linkText={t('organizationUserDetailsWidget.link')}
          count={activatedEmployeesCount}
          showDivider
        />
        <OrganizationUserDetailsWidgetItem
          onClick={handleInvitedUsersLinkClick}
          title={t('organizationUserDetailsWidget.invitedUsers.title')}
          titleHelperText={t('organizationUserDetailsWidget.invitedUsers.tooltip')}
          linkText={t('organizationUserDetailsWidget.link')}
          count={provisionedEmployeesCount}
          showDivider={false}
        />
      </Grid>
    </WidgetContainer>
  );
};

export default memo(OrganizationUserDetailsWidget);
